import React, { useState, useEffect, useCallback } from 'react';
import { Card, Table, Button, Spinner, Badge, Modal, Form, Alert } from 'react-bootstrap';
import { useAuth } from '../../../../contexts/AuthContext';
import { FaTrash, FaExternalLinkAlt } from 'react-icons/fa';
import { formatTimeAgo } from '../../../utils/communityUtils';
import { Link } from 'react-router-dom';

const STATUS_VARIANTS = {
  published: 'success',
  held_for_review: 'warning',
  rejected: 'danger',
  deleted: 'secondary',
};

export default function AdminAllDiscussions() {
  const { token } = useAuth();
  const [discussions, setDiscussions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('published');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);

  // Delete confirmation modal
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const fetchDiscussions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetch(
        `${process.env.REACT_APP_BASE_URL || ''}/api/community/admin/discussions?status=${statusFilter}&limit=20&page=${page}&search=${encodeURIComponent(debouncedSearch)}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      const data = await res.json();
      if (data.success) {
        setDiscussions(data.discussions || []);
        setTotalPages(data.totalPages || 1);
        setTotalCount(data.totalCount || 0);
      } else {
        setError(data.error || 'Failed to load discussions');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [token, statusFilter, page, debouncedSearch]);

  // Debounce search input
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearch(search), 500);
    return () => clearTimeout(handler);
  }, [search]);

  // Reset page to 1 when filter or search changes
  useEffect(() => {
    setPage(1);
  }, [statusFilter, debouncedSearch]);

  useEffect(() => {
    fetchDiscussions();
  }, [fetchDiscussions]);

  const confirmDelete = (discussion) => {
    setDeleteTarget(discussion);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setActionLoading(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BASE_URL || ''}/api/community/discussions/${deleteTarget.id}`,
        {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      const data = await res.json();
      if (data.success) {
        setDiscussions(prev => prev.filter(d => d.id !== deleteTarget.id));
        setShowDeleteModal(false);
        setDeleteTarget(null);
      } else {
        setError(data.error || 'Failed to delete');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="py-4 community-admin-container">
      <div className="d-flex justify-content-between align-items-start mb-4">
        <div>
          <h2>All Discussions</h2>
          <p className="text-muted mb-0 small">Browse and manage all community posts across all statuses.</p>
        </div>
        <Badge bg="secondary" className="fs-6">{totalCount} total</Badge>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>
      )}

      {/* Filters */}
      <div className="d-flex gap-3 mb-4 flex-wrap align-items-center">
        <div className="d-flex gap-2">
          {['published', 'held_for_review', 'rejected', 'deleted', 'all'].map(s => (
            <Button
              key={s}
              size="sm"
              variant={statusFilter === s ? 'warning' : 'outline-secondary'}
              onClick={() => setStatusFilter(s)}
            >
              {s === 'held_for_review' ? 'Pending' : s.charAt(0).toUpperCase() + s.slice(1)}
            </Button>
          ))}
        </div>
        <div style={{ maxWidth: '280px', flex: 1 }}>
          <Form.Control
            type="search"
            placeholder="Search by title or author..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="rounded-pill"
          />
        </div>
      </div>

      <Card className="shadow-sm border-0">
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="warning" />
            </div>
          ) : (
            <Table responsive hover className="mb-0">
              <thead className="bg-light">
                <tr>
                  <th>Status</th>
                  <th>Title</th>
                  <th>Author</th>
                  <th>Category ID</th>
                  <th className="text-center">Replies</th>
                  <th className="text-center">Views</th>
                  <th>Posted</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {discussions.length > 0 ? (
                  discussions.map(d => (
                    <tr key={d.id}>
                      <td>
                        <Badge bg={STATUS_VARIANTS[d.status] || 'secondary'}>
                          {d.status === 'held_for_review' ? 'Pending' : d.status}
                        </Badge>
                      </td>
                      <td style={{ maxWidth: '260px' }} className="text-truncate">
                        <span title={d.title}>{d.title}</span>
                      </td>
                      <td className="small text-muted">{d.authorName || d.authorId}</td>
                      <td className="small text-muted">{d.communityId || '—'}</td>
                      <td className="text-center">{d.replyCount || 0}</td>
                      <td className="text-center">{d.viewCount || 0}</td>
                      <td className="small text-muted">{formatTimeAgo(d.createdAt)}</td>
                      <td className="text-end">
                        {d.status === 'published' && (
                          <Link
                            to={`/community/thread/${d.id}`}
                            className="btn btn-outline-info btn-sm me-1"
                            target="_blank"
                            rel="noopener noreferrer"
                            title="View live post"
                          >
                            <FaExternalLinkAlt />
                          </Link>
                        )}
                        {d.status !== 'deleted' && (
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => confirmDelete(d)}
                            title="Delete this post"
                          >
                            <FaTrash />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="8" className="text-center py-5 text-muted">
                      No discussions found.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="d-flex justify-content-center mt-4">
          <Button 
            variant="outline-secondary" 
            size="sm" 
            className="me-2" 
            disabled={page === 1}
            onClick={() => setPage(p => Math.max(1, p - 1))}
          >
            Previous
          </Button>
          <span className="align-self-center mx-3">Page {page} of {totalPages}</span>
          <Button 
            variant="outline-secondary" 
            size="sm" 
            disabled={page === totalPages}
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
          >
            Next
          </Button>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteModal}
        onHide={() => { setShowDeleteModal(false); setDeleteTarget(null); }}
        centered
        contentClassName="bg-dark border border-secondary"
      >
        <Modal.Body className="text-center py-5 px-4">
          <FaTrash style={{ fontSize: '3rem', color: '#dc3545' }} className="mb-4" />
          <h4 className="text-white mb-2">Delete this discussion?</h4>
          {deleteTarget && (
            <p className="text-warning mb-3 small fst-italic">"{deleteTarget.title}"</p>
          )}
          <p className="text-muted mb-4">
            The post will be soft-deleted and removed from public view. This action cannot be reversed from the admin panel.
          </p>
          <div className="d-flex gap-3 justify-content-center">
            <Button variant="outline-secondary" onClick={() => { setShowDeleteModal(false); setDeleteTarget(null); }} disabled={actionLoading}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDelete} disabled={actionLoading}>
              {actionLoading ? <Spinner size="sm" className="me-2" /> : <FaTrash className="me-2" />}
              Delete Post
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
}
